// release-secrets-stdin-reader.ts — phase 04b (spec V2 §4.2 "Secret release —
// stdin, chỉ trong bộ nhớ").
//
// `avatar autopilot run --release-secrets-stdin` đọc stdin tới EOF TRƯỚC vòng
// supervisor, cùng định dạng `test.env` (parser của 04a: `KEY=VALUE`, không
// interpolation, không `export`). Map chỉ sống trong bộ nhớ process `run`:
// không ghi file nào, không vào state/journal — đi thẳng tới recipe release
// sau G3 approved (recipe-env-source-merger.ts). Từ chối (caller exit ≠ 0,
// chưa dispatch) khi:
//  - stdin là TTY — quy ước "secret chỉ qua pipe" của CLI; KHÔNG đọc gì;
//  - quá trần / dòng sai dạng / key bị từ chối / không có key nào — dòng sai
//    dạng sẽ không được che theo giá trị, nên không nhận nửa vời.
// Thông điệp chỉ nêu số dòng / tên key, không bao giờ giá trị.
// Review 04b: L1 — in 1 dòng stderr trước khi đọc; pipe nhàn rỗi quá hạn
// (mặc định 120 s, chỉnh qua tham số hàm — không qua env/CLI) ⇒ từ chối, chưa
// dispatch. L2 — KHÔNG trim toàn input: parser đã bỏ dòng trống/CRLF/BOM, nên
// giá trị và số dòng báo lỗi khớp `test.env`. L3 — giá trị < 8 ký tự ⇒ stderr
// nêu tên key "không được che theo giá trị".
import type { Readable } from "node:stream";
import { readStdinUntilEof, StdinIdleTimeoutError } from "../read-secret-from-stdin.js";
import { KNOWN_SECRET_VALUE_MIN_LENGTH } from "./known-secret-value-encoded-variants.js";
import {
  keysWithValuesTooShortToMask,
  parseMissionTestEnvText,
} from "./mission-secrets-test-env-parser-and-permissions.js";

export type ReleaseSecretsStdin = Readonly<Record<string, string>>;

export type ReleaseSecretsStdinReadResult =
  | { ok: true; values: ReleaseSecretsStdin }
  | { ok: false; message: string };

const FLAG = "--release-secrets-stdin";

/** L1: không nhận byte nào trong chừng này ⇒ từ chối (vault CLI hỏi MFA vẫn kịp). */
export const RELEASE_SECRETS_STDIN_IDLE_TIMEOUT_MS = 120_000;

export interface ReleaseSecretsStdinReadOptions {
  idleTimeoutMs?: number;
  /** Một dòng thông báo (không bao giờ chứa giá trị) — mặc định ghi stderr. */
  stderr?: (line: string) => void;
}

function writeStderrLine(line: string): void {
  process.stderr.write(`${line}\n`);
}

function refuse(reason: string): ReleaseSecretsStdinReadResult {
  return { ok: false, message: `${FLAG}: ${reason} — chưa dispatch.` };
}

export async function readReleaseSecretsFromStdin(
  stream: Readable & { isTTY?: boolean } = process.stdin,
  options: ReleaseSecretsStdinReadOptions = {},
): Promise<ReleaseSecretsStdinReadResult> {
  if (stream.isTTY === true) {
    return refuse(
      "stdin là TTY — chỉ nhận pipe (vd `secret-tool … | avatar autopilot run … " +
        `${FLAG}\`), không gõ secret vào terminal`,
    );
  }
  const idleTimeoutMs = options.idleTimeoutMs ?? RELEASE_SECRETS_STDIN_IDLE_TIMEOUT_MS;
  const stderr = options.stderr ?? writeStderrLine;
  stderr(
    `${FLAG}: đang đọc secret release từ stdin tới EOF… (chỉ giữ trong bộ nhớ; ` +
      `${idleTimeoutMs / 1000} s không nhận byte nào ⇒ từ chối)`,
  );
  let text: string;
  try {
    text = await readStdinUntilEof(stream, { idleTimeoutMs });
  } catch (err) {
    // Không chuyển tiếp err.message: không bao giờ in nội dung đã đọc.
    if (err instanceof StdinIdleTimeoutError) {
      return refuse(`không nhận byte nào trong ${idleTimeoutMs / 1000} s (pipe chưa đóng?)`);
    }
    return refuse("không đọc được stdin (quá trần 64 KiB hoặc lỗi đọc)");
  }
  const parsed = parseMissionTestEnvText(text);
  if (parsed.malformedLines.length > 0) {
    return refuse(
      `dòng ${parsed.malformedLines.join(", ")} không đúng dạng KEY=VALUE ` +
        "(không `export`, không khoảng trắng quanh `=`)",
    );
  }
  if (parsed.rejectedKeys.length > 0) {
    return refuse(`key bị từ chối: ${parsed.rejectedKeys.join(", ")}`);
  }
  if (Object.keys(parsed.values).length === 0) {
    return refuse("stdin không có dòng KEY=VALUE nào");
  }
  const short = keysWithValuesTooShortToMask(parsed.values);
  if (short.length > 0) {
    const min = KNOWN_SECRET_VALUE_MIN_LENGTH;
    stderr(`${FLAG}: giá trị < ${min} ký tự không được che theo giá trị: ${short.join(", ")}`);
  }
  return { ok: true, values: parsed.values };
}
